import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ProfileSkillsInterestsProps {
  items: string[];
  isOwnProfile: boolean;
  newItem: string;
  onNewItemChange: (value: string) => void;
  onAddItem: () => void;
  onRemoveItem: (item: string) => void;
}

export const ProfileSkillsInterests = ({
  items,
  isOwnProfile,
  newItem,
  onNewItemChange,
  onAddItem,
  onRemoveItem,
}: ProfileSkillsInterestsProps) => {
  return (
    <div className="bg-flux-black/50 border border-flux-cyan/20 rounded-lg p-6">
      <div className="flex flex-wrap gap-2 mb-4">
        {items.map((item, index) => (
          <Badge 
            key={index} 
            variant="secondary" 
            className="bg-flux-purple/20 text-flux-purple flex items-center gap-1"
          >
            {item}
            {isOwnProfile && (
              <button
                onClick={() => onRemoveItem(item)}
                className="hover:text-flux-purple/80 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {isOwnProfile && (
        <div className="flex gap-2">
          <Input
            value={newItem}
            onChange={(e) => onNewItemChange(e.target.value)}
            placeholder="Add a new item"
            className="bg-transparent border-flux-cyan/20"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onAddItem();
              }
            }}
          />
          <Button
            size="icon"
            onClick={onAddItem}
            className="text-flux-purple hover:text-flux-purple/80"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export const CombinedSkillsInterests = ({
  skills,
  interests,
  isOwnProfile,
  newSkill,
  newInterest,
  onNewSkillChange,
  onNewInterestChange,
  onAddSkill,
  onAddInterest,
  onRemoveSkill,
  onRemoveInterest,
}: {
  skills: string[];
  interests: string[];
  isOwnProfile: boolean;
  newSkill: string;
  newInterest: string;
  onNewSkillChange: (value: string) => void;
  onNewInterestChange: (value: string) => void;
  onAddSkill: () => void;
  onAddInterest: () => void;
  onRemoveSkill: (skill: string) => void;
  onRemoveInterest: (interest: string) => void;
}) => {
  return (
    <div className="mt-8">
      <Tabs defaultValue="skills" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-flux-black/50">
          <TabsTrigger value="skills" className="text-white data-[state=active]:bg-flux-cyan/20">
            Skills
          </TabsTrigger>
          <TabsTrigger value="interests" className="text-white data-[state=active]:bg-flux-cyan/20">
            Interests
          </TabsTrigger>
        </TabsList>
        <TabsContent value="skills">
          <ProfileSkillsInterests
            items={skills}
            isOwnProfile={isOwnProfile}
            newItem={newSkill}
            onNewItemChange={onNewSkillChange}
            onAddItem={onAddSkill}
            onRemoveItem={onRemoveSkill}
          />
        </TabsContent>
        <TabsContent value="interests">
          <ProfileSkillsInterests
            items={interests}
            isOwnProfile={isOwnProfile}
            newItem={newInterest}
            onNewItemChange={onNewInterestChange}
            onAddItem={onAddInterest}
            onRemoveItem={onRemoveInterest}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};